import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowDownAZ, ArrowUpAZ, ExternalLink, ShieldCheck, ShieldOff } from 'lucide-react';
import { useUrlFilters } from '../../hooks/admin/useUrlFilters';
import { useGrowthAccounts, useGrowthOverview } from '../../hooks/admin/useAdminGrowth';
import { useSetOrgStatus } from '../../hooks/admin/useAdminUsers';
import { enumField, pageField, stringField, weeksPreset } from '../../lib/adminFilters';
import { chartColors } from '../../lib/chartColors';
import { getApiErrorMessage } from '../../lib/apiError';
import { absoluteTime, prettyStage, relativeTime } from '../../lib/adminFormat';
import { formatDate, formatDateTime } from '../../lib/utils';
import DataTable from '../../components/ui/DataTable';
import StatTile from '../../components/admin/StatTile';
import DateRangeFilter from '../../components/admin/DateRangeFilter';
import SeriesChart from '../../components/admin/SeriesChart';
import FunnelBars from '../../components/admin/FunnelBars';
import Pagination from '../../components/admin/Pagination';
import InlineNotice from '../../components/admin/InlineNotice';
import ConfirmActionDialog from '../../components/admin/ConfirmActionDialog';
import StatusBadge, { Pill, RolePill } from '../../components/admin/StatusBadge';
import type { GrowthAccountRow, OrgStatus } from '../../types/admin';

const PAGE_SIZE = 25;
const WEEKS_OPTIONS = [
  { value: '4', label: '4w' },
  { value: '12', label: '12w' },
  { value: '26', label: '26w' },
] as const;

const AGENCY_STAGES = [
  'SIGNED_UP',
  'ORG_CREATED',
  'FIRST_KOL',
  'FIRST_CAMPAIGN',
  'FIRST_BROADCAST',
  'FIRST_CONTRACT_SIGNED',
  'FIRST_INVOICE',
] as const;
const BRAND_STAGES = ['SIGNED_UP', 'ORG_CREATED', 'FIRST_CAMPAIGN_LINKED', 'FIRST_SCRIPT_APPROVED'] as const;
const ALL_STAGES = Array.from(new Set<string>([...AGENCY_STAGES, ...BRAND_STAGES]));

const SORT_OPTIONS = [
  { value: 'createdAt', label: 'Created' },
  { value: 'lastActivityAt', label: 'Last activity' },
  { value: 'name', label: 'Name' },
  { value: 'campaigns', label: 'Campaigns' },
  { value: 'requests7d', label: 'Requests 7d' },
  { value: 'helpChatQuestions', label: 'Help questions' },
] as const;

const growthSchema = {
  weeks: enumField(['4', '12', '26'], '12'),
  type: enumField(['', 'AGENCY', 'BRAND'], ''),
  stage: stringField(''),
  activity: enumField(['', 'active', 'dormant', 'never'], ''),
  sort: enumField(['createdAt', 'lastActivityAt', 'name', 'campaigns', 'requests7d', 'helpChatQuestions'], 'createdAt'),
  dir: enumField(['desc', 'asc'], 'desc'),
  search: stringField('', { maxLength: 200 }),
  page: pageField(),
};

function weekLabel(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-MY', { month: 'short', day: 'numeric' });
}

function typeTone(type: string): 'info' | 'warn' | 'muted' {
  if (type === 'AGENCY') return 'info';
  if (type === 'BRAND') return 'warn';
  return 'muted';
}

export default function AdminGrowth() {
  const navigate = useNavigate();
  const { filters, setFilters, resetFilters } = useUrlFilters(growthSchema);
  const weeks = weeksPreset(filters.weeks);
  const posthogUrl = import.meta.env.VITE_POSTHOG_PROJECT_URL?.trim();

  const [searchInput, setSearchInput] = useState(filters.search);
  useEffect(() => setSearchInput(filters.search), [filters.search]);
  useEffect(() => {
    if (searchInput === filters.search) return;
    const t = setTimeout(() => setFilters({ search: searchInput }), 300);
    return () => clearTimeout(t);
  }, [searchInput, filters.search, setFilters]);

  const overviewQuery = useGrowthOverview(weeks);
  const accountsQuery = useGrowthAccounts({
    type: filters.type || undefined,
    stage: filters.stage || undefined,
    activity: filters.activity || undefined,
    sort: filters.sort,
    dir: filters.dir,
    search: filters.search || undefined,
    page: filters.page,
    limit: PAGE_SIZE,
  });

  const overview = overviewQuery.data;
  const activity = overview?.activity;
  const accounts = accountsQuery.data?.items ?? [];
  const usersWithoutOrg = accountsQuery.data?.usersWithoutOrg ?? [];

  const signupsData = useMemo(() => overview?.signupsPerWeek ?? [], [overview]);
  const orgsData = useMemo(() => overview?.orgsPerWeek ?? [], [overview]);

  const orgStatus = useSetOrgStatus();
  const [pendingOrg, setPendingOrg] = useState<{ row: GrowthAccountRow; status: OrgStatus } | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const runOrgStatus = async (reason: string) => {
    if (!pendingOrg) return;
    try {
      await orgStatus.mutateAsync({ organizationId: pendingOrg.row.id, status: pendingOrg.status, reason: reason || undefined });
      setNotice({ tone: 'success', text: `${pendingOrg.row.name} is now ${pendingOrg.status}.` });
      setPendingOrg(null);
    } catch {
      /* shown in dialog */
    }
  };

  const openRow = (row: GrowthAccountRow) => {
    if (row.type === 'AGENCY') navigate(`/admin/agencies/${row.id}`);
    else navigate(`/admin/users?organizationId=${encodeURIComponent(row.id)}`);
  };

  const hasFilters = Boolean(filters.type || filters.stage || filters.activity || filters.search);

  return (
    <div className="space-y-6 text-glass-text">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="page-title">Growth</h1>
          <p className="mt-1 text-sm text-glass-text-2">Signups, activation funnels and what every account has done since joining.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <DateRangeFilter value={filters.weeks} options={WEEKS_OPTIONS} onChange={(v) => setFilters({ weeks: v })} label="Window" />
          {posthogUrl && (
            <a href={posthogUrl} target="_blank" rel="noreferrer" className="btn-glass inline-flex items-center gap-1.5 px-3 py-1.5 text-sm">
              <ExternalLink className="h-4 w-4" aria-hidden />
              Open PostHog
            </a>
          )}
        </div>
      </div>

      {overviewQuery.isError && <InlineNotice tone="error">{getApiErrorMessage(overviewQuery.error, 'Failed to load growth overview')}</InlineNotice>}
      {notice && (
        <InlineNotice tone={notice.tone} onDismiss={() => setNotice(null)}>
          {notice.text}
        </InlineNotice>
      )}

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <StatTile
          label="Active (7d)"
          value={(activity?.active7 ?? 0).toLocaleString()}
          tone="pos"
          loading={overviewQuery.isPending}
          sub={`of ${overview?.totals.orgs ?? 0} organisations`}
          onClick={() => setFilters({ activity: 'active' })}
        />
        <StatTile label="Active (30d)" value={(activity?.active30 ?? 0).toLocaleString()} loading={overviewQuery.isPending} sub={`${overview?.totals.users ?? 0} users total`} />
        <StatTile
          label="Dormant (14d+)"
          value={(activity?.dormant14d ?? 0).toLocaleString()}
          tone={(activity?.dormant14d ?? 0) > 0 ? 'warn' : 'default'}
          loading={overviewQuery.isPending}
          sub="no activity for two weeks"
          onClick={() => setFilters({ activity: 'dormant' })}
          title="Show dormant accounts"
        />
        <StatTile
          label="Never activated"
          value={(activity?.neverActivated ?? 0).toLocaleString()}
          tone={(activity?.neverActivated ?? 0) > 0 ? 'danger' : 'default'}
          loading={overviewQuery.isPending}
          sub={`${activity?.usersWithoutOrg ?? 0} users without an org`}
          onClick={() => setFilters({ activity: 'never' })}
          title="Show accounts that never reached a first campaign"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="glass-card p-5">
          <h3 className="mb-3 text-sm font-semibold text-glass-text">Signups per week</h3>
          <SeriesChart
            data={signupsData}
            xKey="week"
            xFormatter={weekLabel}
            loading={overviewQuery.isPending}
            series={[
              { key: 'AGENCY', name: 'Agency', color: chartColors.accent },
              { key: 'BRAND', name: 'Brand', color: chartColors.info },
              { key: 'KOL', name: 'KOL', color: chartColors.pos },
            ]}
          />
        </div>
        <div className="glass-card p-5">
          <h3 className="mb-3 text-sm font-semibold text-glass-text">Organisations created per week</h3>
          <SeriesChart
            data={orgsData}
            xKey="week"
            xFormatter={weekLabel}
            loading={overviewQuery.isPending}
            series={[
              { key: 'AGENCY', name: 'Agency', color: chartColors.accent },
              { key: 'BRAND', name: 'Brand', color: chartColors.info },
            ]}
          />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <FunnelBars
          title="Agency activation funnel"
          stages={overview?.funnel.agency ?? []}
          loading={overviewQuery.isPending}
          onStageClick={(stage) => setFilters({ type: 'AGENCY', stage })}
        />
        <FunnelBars
          title="Brand activation funnel"
          stages={overview?.funnel.brand ?? []}
          loading={overviewQuery.isPending}
          onStageClick={(stage) => setFilters({ type: 'BRAND', stage })}
        />
      </div>

      <div className="glass-card space-y-3 p-4">
        <div className="grid gap-3 md:grid-cols-5">
          <input type="search" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search organisation or owner" className="glass-input w-full px-4 py-2 md:col-span-2" />
          <select value={filters.type} onChange={(e) => setFilters({ type: e.target.value as typeof filters.type })} className="glass-input w-full px-3 py-2 text-sm">
            <option value="">All types</option>
            <option value="AGENCY">Agencies</option>
            <option value="BRAND">Brands</option>
          </select>
          <select value={filters.stage} onChange={(e) => setFilters({ stage: e.target.value })} className="glass-input w-full px-3 py-2 text-sm">
            <option value="">All stages</option>
            {ALL_STAGES.map((s) => (
              <option key={s} value={s}>
                {prettyStage(s)}
              </option>
            ))}
          </select>
          <select value={filters.activity} onChange={(e) => setFilters({ activity: e.target.value as typeof filters.activity })} className="glass-input w-full px-3 py-2 text-sm">
            <option value="">Any activity</option>
            <option value="active">Active (7d)</option>
            <option value="dormant">Dormant (14d+)</option>
            <option value="never">Never activated</option>
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm text-glass-text-2">
          <label className="inline-flex items-center gap-2">
            Sort by
            <select value={filters.sort} onChange={(e) => setFilters({ sort: e.target.value as typeof filters.sort })} className="glass-input px-3 py-1.5 text-sm">
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => setFilters({ dir: filters.dir === 'desc' ? 'asc' : 'desc' })}
            className="btn-glass inline-flex items-center gap-1 px-2.5 py-1.5 text-xs"
            title={filters.dir === 'desc' ? 'Descending' : 'Ascending'}
          >
            {filters.dir === 'desc' ? <ArrowDownAZ className="h-4 w-4" aria-hidden /> : <ArrowUpAZ className="h-4 w-4" aria-hidden />}
            {filters.dir === 'desc' ? 'Desc' : 'Asc'}
          </button>
          {hasFilters && (
            <button type="button" onClick={() => resetFilters(['weeks'])} className="text-xs text-accent2 underline-offset-2 hover:underline">
              Clear filters
            </button>
          )}
        </div>
      </div>

      {accountsQuery.isError && <InlineNotice tone="error">{getApiErrorMessage(accountsQuery.error, 'Failed to load accounts')}</InlineNotice>}

      <div className={`glass-card overflow-hidden ${accountsQuery.isFetching && !accountsQuery.isPending ? 'opacity-80' : ''}`}>
        <DataTable<GrowthAccountRow>
          data={accounts}
          isLoading={accountsQuery.isPending}
          emptyMessage="No organisations match these filters."
          onRowClick={openRow}
          columns={[
            {
              header: 'Organisation',
              accessor: (r) => (
                <div className="flex items-center gap-2">
                  <span className="max-w-[220px] truncate font-medium text-glass-text" title={r.name}>
                    {r.name}
                  </span>
                  <Pill tone={typeTone(r.type)}>{r.type}</Pill>
                </div>
              ),
            },
            { header: 'Status', accessor: (r) => <StatusBadge status={r.status} /> },
            {
              header: 'Owner',
              accessor: (r) => (
                <span className="block max-w-[200px] truncate text-glass-text-2" title={r.ownerName ?? undefined}>
                  {r.ownerEmail || r.ownerName || '—'}
                </span>
              ),
            },
            { header: 'Created', accessor: (r) => <span className="text-glass-text-2">{formatDate(r.createdAt)}</span> },
            {
              header: 'Last activity',
              accessor: (r) => (
                <span className="text-glass-text-2" title={absoluteTime(r.lastActivityAt)}>
                  {relativeTime(r.lastActivityAt, 'never')}
                </span>
              ),
            },
            {
              header: 'Stage',
              accessor: (r) => (
                <span className="text-xs text-glass-text" title={r.stageReachedAt ? `Reached ${formatDateTime(r.stageReachedAt)}` : undefined}>
                  {prettyStage(r.stage)}
                </span>
              ),
            },
            { header: 'Members', accessor: (r) => r.counts.members, className: 'tabular-nums text-glass-text-2' },
            { header: 'KOLs', accessor: (r) => r.counts.kols, className: 'tabular-nums text-glass-text-2' },
            { header: 'Campaigns', accessor: (r) => r.counts.campaigns, className: 'tabular-nums text-glass-text-2' },
            { header: 'Invites', accessor: (r) => r.counts.invitations, className: 'tabular-nums text-glass-text-2' },
            { header: 'Contracts', accessor: (r) => r.counts.contractsSigned, className: 'tabular-nums text-glass-text-2' },
            {
              header: 'Help Qs',
              accessor: (r) =>
                r.helpChatQuestions > 0 ? (
                  <Link
                    to={`/admin/chatbot-log?organizationId=${encodeURIComponent(r.id)}`}
                    onClick={(e) => e.stopPropagation()}
                    className="tabular-nums text-accent2 hover:underline"
                  >
                    {r.helpChatQuestions}
                  </Link>
                ) : (
                  <span className="text-glass-text-3">0</span>
                ),
            },
            { header: 'Req 7d', accessor: (r) => r.requests7d.toLocaleString(), className: 'tabular-nums text-glass-text-2' },
            {
              header: '',
              accessor: (r) =>
                r.status === 'SUSPENDED' ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      orgStatus.reset();
                      setPendingOrg({ row: r, status: 'ACTIVE' });
                    }}
                    className="btn-glass inline-flex items-center gap-1 px-2.5 py-1 text-xs text-[rgb(var(--pos))]"
                  >
                    <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
                    Reactivate
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      orgStatus.reset();
                      setPendingOrg({ row: r, status: 'SUSPENDED' });
                    }}
                    className="btn-glass inline-flex items-center gap-1 px-2.5 py-1 text-xs text-[rgb(var(--warn))]"
                  >
                    <ShieldOff className="h-3.5 w-3.5" aria-hidden />
                    Suspend
                  </button>
                ),
            },
          ]}
        />
        <Pagination pagination={accountsQuery.data?.pagination} onPageChange={(page) => setFilters({ page })} label="organisations" isFetching={accountsQuery.isFetching && !accountsQuery.isPending} />
      </div>

      <div className="glass-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-glass px-5 py-3">
          <h3 className="text-sm font-semibold text-glass-text">Users without an organisation</h3>
          <span className="text-xs text-glass-text-3">signed up but never finished onboarding</span>
        </div>
        {usersWithoutOrg.length === 0 ? (
          <p className="px-5 py-6 text-sm text-glass-text-3">{accountsQuery.isPending ? 'Loading…' : 'Everyone belongs to an organisation.'}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-glass-1 text-left text-[0.7rem] uppercase tracking-[0.05em] text-glass-text-2">
                <tr>
                  <th className="px-5 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Email</th>
                  <th className="px-3 py-2 font-medium">Role</th>
                  <th className="px-3 py-2 font-medium">Source</th>
                  <th className="px-3 py-2 font-medium">Created</th>
                  <th className="px-5 py-2 font-medium">Last seen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-glass">
                {usersWithoutOrg.map((u) => (
                  <tr key={u.id} className="hover:bg-glass-3/40">
                    <td className="px-5 py-2.5 text-glass-text">{u.fullName || 'Unnamed user'}</td>
                    <td className="px-3 py-2.5">
                      {u.email ? (
                        <Link to={`/admin/users?search=${encodeURIComponent(u.email)}`} className="text-accent2 hover:underline">
                          {u.email}
                        </Link>
                      ) : (
                        <span className="text-glass-text-3">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <RolePill role={u.role} />
                    </td>
                    <td className="px-3 py-2.5 text-glass-text-2">{u.signupSource || '—'}</td>
                    <td className="px-3 py-2.5 text-glass-text-2">{formatDate(u.createdAt)}</td>
                    <td className="px-5 py-2.5 text-glass-text-2" title={absoluteTime(u.lastSeenAt)}>
                      {relativeTime(u.lastSeenAt, 'never')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pendingOrg && (
        <ConfirmActionDialog
          isOpen
          onClose={() => setPendingOrg(null)}
          title={`${pendingOrg.status === 'SUSPENDED' ? 'Suspend' : 'Reactivate'} ${pendingOrg.row.name}`}
          description={
            pendingOrg.status === 'SUSPENDED'
              ? 'Every member of this organisation (except platform admins) is blocked from myKOL until it is reactivated. Members are signed out on their next request.'
              : 'Members of this organisation can use myKOL again immediately.'
          }
          confirmLabel={pendingOrg.status === 'SUSPENDED' ? 'Suspend organisation' : 'Reactivate'}
          tone={pendingOrg.status === 'SUSPENDED' ? 'warn' : 'default'}
          requireReason={pendingOrg.status === 'SUSPENDED'}
          optionalReason={pendingOrg.status !== 'SUSPENDED'}
          isPending={orgStatus.isPending}
          error={orgStatus.isError ? getApiErrorMessage(orgStatus.error, 'Failed to update organisation status') : null}
          onConfirm={runOrgStatus}
        />
      )}
    </div>
  );
}
